import type { VariantWithStockDTO } from "@/modules/inventory/domain/contracts";
import {
  Button,
  FormNumber,
  FormRoot,
  FormTextarea,
  Input,
  Label,
  Modal,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { ArrowUpDownIcon, SearchIcon, XIcon } from "lucide-react";
import { useEffect } from "react";
import { z } from "zod";
import { useAdjustStockMutation } from "../api/queries";

const StockAdjustFormSchema = z.object({
  variantId: z.string().min(1),
  adjustmentKind: z.enum(["add", "subtract", "restock"]),
  quantityDelta: z
    .number({ message: "ຈຳນວນຕ້ອງເປັນຕົວເລກ" })
    .refine((val) => val !== 0, { message: "ຈຳນວນຕ້ອງບໍ່ເປັນສູນ" }),
  note: z.string().optional(),
});

type StockAdjustFormValues = z.infer<typeof StockAdjustFormSchema>;

type StockAdjustDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  variant?: VariantWithStockDTO | null;
  onSuccess?: () => void;
};

function productNameDisplay(
  name: VariantWithStockDTO["product"]["name"],
): string {
  if (!name || typeof name !== "object") return "—";
  const n = name as { lo?: string; th?: string; en?: string };
  return n.lo ?? n.th ?? n.en ?? "—";
}

export function StockAdjustDialog({
  open,
  onOpenChange,
  variant,
  onSuccess,
}: StockAdjustDialogProps) {
  const adjustStock = useAdjustStockMutation();

  const methods = RHF.useForm<StockAdjustFormValues>({
    resolver: zodResolver(StockAdjustFormSchema),
    defaultValues: {
      variantId: variant?.variantId ?? "",
      adjustmentKind: "add",
      quantityDelta: 0,
      note: "",
    },
    mode: "onTouched",
  });

  // Reset เฉพาะตอนเปิด dialog หรือเปลี่ยน variant (ບໍ່ໃສ່ methods ເພາະ reference ປ່ຽນແລ້ວ effect ຈະວິງ reset ທຸກ re-render ເຮັດໃຫ້ການເລືອກ type ກັບໄປເປັນ Add)
  useEffect(() => {
    if (open) {
      methods.reset({
        variantId: variant?.variantId ?? "",
        adjustmentKind: "add",
        quantityDelta: 0,
        note: "",
      });
    }
  }, [open, variant?.variantId]);

  const adjustmentKind = methods.watch("adjustmentKind");

  const onSubmit = async (values: StockAdjustFormValues) => {
    const isRestock = values.adjustmentKind === "restock";
    const payload = isRestock
      ? {
          variantId: values.variantId,
          type: "restock" as const,
          quantity: Math.abs(values.quantityDelta),
          note: values.note?.trim() || undefined,
        }
      : {
          variantId: values.variantId,
          type: "adjustment" as const,
          quantityDelta:
            values.adjustmentKind === "subtract"
              ? -Math.abs(values.quantityDelta)
              : Math.abs(values.quantityDelta),
          note: values.note?.trim() || undefined,
        };
    await adjustStock.mutateAsync(payload);
    onSuccess?.();
    onOpenChange(false);
    methods.reset();
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange} showCloseButton={false}>
      <FormRoot methods={methods} onSubmit={onSubmit}>
        <div className="space-y-5">
          {/* Header */}
          <div className="flex items-start justify-between border-b pb-4">
            <h3 className="font-semibold text-lg">ປັບສະຕ໋ອກແບບມື</h3>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label="ປິດ"
            >
              <XIcon className="h-5 w-5" />
            </button>
          </div>

          <input type="hidden" {...methods.register("variantId")} />

          {/* ເລືອກຕົວແປ */}
          <div className="space-y-2">
            <Label className="text-muted-foreground text-xs uppercase tracking-wide">
              ເລືອກຕົວແປ
            </Label>
            <div className="relative">
              <SearchIcon className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                readOnly
                value={
                  variant
                    ? `${productNameDisplay(variant.product.name)} • ${variant.sku}`
                    : ""
                }
                placeholder="ຄົ້ນຫາຕາມ SKU ຫຼື ຊື່..."
                className="h-9 pr-9 pl-9"
              />
              <span className="absolute top-1/2 right-3 -translate-y-1/2 text-muted-foreground">
                <ArrowUpDownIcon className="h-4 w-4" />
              </span>
            </div>
          </div>

          {/* ປະເພດການປັບ */}
          <div className="space-y-2">
            <Label className="text-muted-foreground text-xs uppercase tracking-wide">
              ປະເພດການປັບ
            </Label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => methods.setValue("adjustmentKind", "add")}
                className={`flex-1 rounded-md border-2 px-3 py-2 font-medium text-sm transition-colors ${
                  adjustmentKind === "add"
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-input bg-background hover:bg-muted"
                }`}
              >
                ເພີ່ມ (+)
              </button>
              <button
                type="button"
                onClick={() => methods.setValue("adjustmentKind", "subtract")}
                className={`flex-1 rounded-md border-2 px-3 py-2 font-medium text-sm transition-colors ${
                  adjustmentKind === "subtract"
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-input bg-background hover:bg-muted"
                }`}
              >
                ລົບ (-)
              </button>
              <button
                type="button"
                onClick={() => methods.setValue("adjustmentKind", "restock")}
                className={`flex-1 rounded-md border-2 px-3 py-2 font-medium text-sm transition-colors ${
                  adjustmentKind === "restock"
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-input bg-background hover:bg-muted"
                }`}
              >
                ເຕີມສິນຄ້າ
              </button>
            </div>
          </div>

          {/* ຈຳນວນທີ່ປັບ */}
          <FormNumber
            name="quantityDelta"
            label="ຈຳນວນທີ່ປັບ"
            placeholder="0"
            required
          />

          {/* ເຫດຜົນ / ອ້າງອີງ */}
          <FormTextarea
            name="note"
            label="ເຫດຜົນ / ອ້າງອີງ"
            placeholder="ຕົວຢ່າງ: ເສຍຫາຍຂົນສົ່ງ, ຄືນຜູ້ສະໜອງ..."
            rows={3}
          />

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 border-t pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={adjustStock.isPending}
            >
              ຍົກເລີກ
            </Button>
            <Button type="submit" disabled={adjustStock.isPending || !variant}>
              {adjustStock.isPending ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກການປ່ຽນແປງ"}
            </Button>
          </div>
        </div>
      </FormRoot>
    </Modal>
  );
}
